import {minus, numUtil} from '@/helper/num';
import {spotOrderUtil} from '@/helper/order-spot';
import {convertPrice} from './util';
import type {ws, SpotPair, OrderNotificationParams} from '@/types';
type OpenSpotOrderRaw = ws.OpenSpotOrderRaw;

export function convertConditionalOrder<OrderRaw extends OpenSpotOrderRaw, TradingPair extends SpotPair>(
  order: OrderRaw,
  tradingPairs: TradingPair[],
  unScale: boolean = false
) {
  if (order.bizError !== 0) {
    return {
      ID: 'OrderError',
      Code: order.bizError,
    };
  }

  const {
    side,
    priceEp,
    stopPxEp,
    qtyType,
    baseQtyEv,
    quoteQtyEv,
    leavesBaseQtyEv,
    leavesQuoteQtyEv,
    ordStatus,
    action,
    execStatus,
  } = order;
  const tradingPair = tradingPairs.find((o) => o.symbol === order.symbol);
  if (!tradingPair) {
    return null;
  }
  const {pricePrecision, baseCurrency, quoteCurrency, baseQtyPrecision, quoteQtyPrecision} = tradingPair;
  const quoteQtyScale = unScale ? 0 : tradingPair.quoteQtyScale;
  const baseQtyScale = unScale ? 0 : tradingPair.baseQtyScale;

  const isLiqTrade = spotOrderUtil.isLiqTradeOrder(order);
  const isMarket = spotOrderUtil.isMarketOrder(order);
  const baseQty = numUtil.floor(baseQtyEv, baseQtyPrecision, baseQtyScale);
  const quoteQty = numUtil.floor(quoteQtyEv, quoteQtyPrecision, quoteQtyScale);

  const Price = convertPrice(isMarket || isLiqTrade, priceEp, pricePrecision, quoteQtyScale);
  const convertParams = createOrderParamsFun(
    {QtyType: qtyType, Price, Side: side, Coin: baseCurrency},
    {qtyType, baseQty, baseCurrency, quoteQty, quoteCurrency}
  );

  if (action === 'Replace' && ['Replaced', 'ReAdded'].includes(execStatus)) {
    return convertParams('OrderModify');
  }

  if (ordStatus === 'Untriggered') {
    return convertParams('ConditionalOrderSubmitted', {
      TriggerPrice: numUtil.floor(stopPxEp, pricePrecision, quoteQtyScale),
    });
  }

  if (ordStatus === 'Deactivated') {
    return convertParams('OrderCanceled');
  }

  if (ordStatus === 'New') {
    return convertParams('ConditionalOrderTriggered');
  }

  if (ordStatus === 'PartiallyFilled') {
    const baseRemainingQty = numUtil.floor(leavesBaseQtyEv, baseQtyPrecision, baseQtyScale);
    const baseFilledQty = numUtil.floor(minus(baseQtyEv, leavesBaseQtyEv), baseQtyPrecision, baseQtyScale);
    const quoteRemainingQty = numUtil.floor(leavesQuoteQtyEv, baseQtyPrecision, baseQtyScale);
    const quoteFilledQty = numUtil.floor(minus(quoteQtyEv, leavesQuoteQtyEv), baseQtyPrecision, baseQtyScale);
    const Amount = spotOrderUtil.calcQtyWU(qtyType, baseFilledQty, baseCurrency, quoteFilledQty, quoteCurrency);
    const RemainingAmount = spotOrderUtil.calcQtyWU(
      qtyType,
      baseRemainingQty,
      baseCurrency,
      quoteRemainingQty,
      quoteCurrency
    );
    return convertParams('OrderPartiallyFilled', {Amount, RemainingAmount});
  }

  if (ordStatus === 'Filled') {
    return convertParams('OrderFilled');
  }

  if (ordStatus === 'Canceled') {
    return convertParams('OrderCanceled');
  }

  return null;
}

export function convertNormalOrder<OrderRaw extends OpenSpotOrderRaw, TradingPair extends SpotPair>(
  order: OrderRaw,
  tradingPairs: TradingPair[],
  unScale: boolean = false
) {
  if (order.bizError !== 0) {
    return {
      ID: 'OrderError',
      Code: order.bizError,
    };
  }

  const {
    side,
    priceEp,
    qtyType,
    baseQtyEv,
    quoteQtyEv,
    leavesBaseQtyEv,
    leavesQuoteQtyEv,
    action,
    execStatus,
    ordStatus,
  } = order;
  const tradingPair = tradingPairs.find((o) => o.symbol === order.symbol);
  if (!tradingPair) {
    return null;
  }
  const {pricePrecision, baseCurrency, quoteCurrency, baseQtyPrecision, quoteQtyPrecision} = tradingPair;
  const quoteQtyScale = unScale ? 0 : tradingPair.quoteQtyScale;
  const baseQtyScale = unScale ? 0 : tradingPair.baseQtyScale;

  const isLiqTrade = spotOrderUtil.isLiqTradeOrder(order);
  const isMarket = spotOrderUtil.isMarketOrder(order);
  const baseQty = numUtil.floor(baseQtyEv, baseQtyPrecision, baseQtyScale);
  const quoteQty = numUtil.floor(quoteQtyEv, quoteQtyPrecision, quoteQtyScale);

  const Price = convertPrice(isMarket || isLiqTrade, priceEp, pricePrecision, quoteQtyScale);
  const convertParams = createOrderParamsFun(
    {QtyType: qtyType, Price, Side: side, Coin: baseCurrency},
    {qtyType, baseQty, baseCurrency, quoteQty, quoteCurrency}
  );

  if (action === 'Replace' && ['Replaced', 'ReAdded'].includes(execStatus)) {
    return convertParams('OrderModify');
  }

  if (ordStatus === 'New') {
    return convertParams('OrderSubmitted');
  }

  if (ordStatus === 'PartiallyFilled') {
    const baseRemainingQty = numUtil.floor(leavesBaseQtyEv, baseQtyPrecision, baseQtyScale);
    const baseFilledQty = numUtil.floor(minus(baseQtyEv, leavesBaseQtyEv), baseQtyPrecision, baseQtyScale);
    const quoteRemainingQty = numUtil.floor(leavesQuoteQtyEv, quoteQtyPrecision, quoteQtyScale);
    const quoteFilledQty = numUtil.floor(minus(quoteQtyEv, leavesQuoteQtyEv), quoteQtyPrecision, quoteQtyScale);
    const Amount = spotOrderUtil.calcQtyWU(qtyType, baseFilledQty, baseCurrency, quoteFilledQty, quoteCurrency);
    const RemainingAmount = spotOrderUtil.calcQtyWU(
      qtyType,
      baseRemainingQty,
      baseCurrency,
      quoteRemainingQty,
      quoteCurrency
    );

    return convertParams('OrderPartiallyFilled', {Amount, RemainingAmount});
  }

  if (ordStatus === 'Filled') {
    return convertParams('OrderFilled');
  }

  if (ordStatus === 'Canceled') {
    return convertParams('OrderCanceled');
  }

  return null;
}

function createOrderParamsFun({QtyType, Price, Side, Coin}, {qtyType, baseQty, baseCurrency, quoteQty, quoteCurrency}) {
  return function (
    ID,
    params: {
      Amount?: string;
      TriggerPrice?: string;
      RemainingAmount?: string;
    } = {}
  ): OrderNotificationParams {
    return {
      ...params,
      ID,
      QtyType,
      Price,
      Side,
      Coin,
      Amount: qtyType === 'ByBase' ? baseQty + ' ' + baseCurrency : quoteQty + ' ' + quoteCurrency,
    };
  };
}
